/**
 * ⭐⭐ board 2812 M4 (plan steps 2 and 3, R-2812-a, R-2812-e) — A HOME'S PAGE, THE PARTS
 * NURSING HOMES PUTS BESIDE ITS COLUMN OF HOMES.
 *
 * All homes and a home show the same four tabs (To do, Bookings, Prices, Invoices);
 * `NursingHomes.tsx` owns the column, the tabs and the reads. This file holds what only
 * a home's page or its Prices tab needs:
 *   · `HomeHeading`: the home's name, where it is, and the quiet Details button
 *     beside the title (never a tab, R-2812-e);
 *   · `HomeDetails`: the account's own fields (AccountList's edit form for that ONE
 *     account) and the portal home's settings (pickup offset, phone, who can sign in),
 *     through today's account-keyed and portal-home reads and writes (Q240). The portal
 *     blocks are rendered by the host, which holds their state;
 *   · `HomesPriceCounts`: All homes' Prices, each home with its price count, counted
 *     from the account book's ONE price read (never a call per home);
 *   · `HomePrices`: 2705's `PriceList`, scoped to that account, with its "Remove" /
 *     "Keep it" and its routes unchanged.
 * ⛔ S8: A PER-HOME VIEW IS A LAYOUT, NOT AN ACCESS BOUNDARY (ADR-0009, ADR9-D11).
 * ⛔ No portal home id and no account id reaches the DOM here; an account is found by
 * its group key (the short code or `a<id>`), which `/api/accounts` already serves.
 * ⛔ S7: every row button that shows a home's name carries `data-fb-opaque`.
 */
import type { ComponentChildren } from 'preact';
import { Button, List, Row } from '@ecmb/ui';
import type { ApiAccount, ApiHomeGroup } from '../../lib/actions.js';
import type { AccountBookData } from '../../lib/account-book-loader.js';
import { Banner } from '../../components/Banner.js';
import { AccountList } from './AccountList.js';
import { PriceList } from './PriceList.js';

/** The words a home's page says about itself (R-11: listed for Vicky). */
export const HOME_PAGE_WORDS = {
  allHomes: 'All homes',
  details: 'Details',
  setUp: 'Set up homes and places',
  /** ⭐ board 2812 fix round (Q273(a)): the column's entry for requests no home's page
   *  shows: a portal home linked to no account, or linked to an account not marked as a
   *  nursing home. "Not linked yet" was untrue for the second, so it says neither. */
  notLinked: 'Not set up yet',
  findHome: 'Find a home',
  findHint: 'Name or town',
  /** ⭐ board 2812 M4 fix round (C4): "Find a home" matched nothing. */
  noHomeMatches: 'No home matches that.',
  /** ⭐ board 2812 M4 fix round (C2): a home's title when no read has named it. */
  thisHome: 'This home',
  homesUnread: 'The homes could not be read just now.',
  detailsTitle: 'Details',
  noPortalHome: 'Nobody at this home can sign in to the portal yet. Link it in Set up homes and places.',
  accountUnread: 'This home\'s account could not be read just now, so its details and prices are not shown.',
  /** ⭐ board 2812 M4 fix round: the account read has not answered YET (not a failure). */
  accountLoading: 'Getting the customer accounts…',
  pricesIntro: 'Pick a home to see and change its prices.',
  pricesUnread: 'The prices could not be read just now, so the counts are not shown.',
  pricesCut: 'The price list is longer than the screen can show, so some counts may be short.',
  /** ⭐ board 2812 M5 step 0 (d): All homes' Prices, a home whose account has no town. */
  noTown: 'No town yet',
} as const;

/** "1 price", "18 prices". */
function priceCountWords(n: number): string {
  return n === 1 ? '1 price' : `${String(n)} prices`;
}

/**
 * The account a group stands for: its short code is the key, or `a<account id>` when it
 * has none (`homeKeyOf` on the server). ⚠️ Both spellings are tried because the key is
 * the server's choice: a short code the server would refuse falls back to `a<id>`, and a
 * short code is capitals only, so it can never read as `a<digits>`.
 */
export function accountForKey(accounts: readonly ApiAccount[] | null | undefined, key: string): ApiAccount | null {
  return (accounts ?? []).find((a) => a.shortCode === key || `a${String(a.accountId)}` === key) ?? null;
}

/** "Nursing home in Bray · RCC": where the home is, and its reference letters. */
export function homeSubtitle(group: ApiHomeGroup): string {
  const where = group.town === null || group.town === '' ? 'Nursing home' : `Nursing home in ${group.town}`;
  return /^[A-Z]{2,5}$/.test(group.key) ? `${where} · ${group.key}` : where;
}

/**
 * The home's title row: its name, where it is, and the quiet Details button.
 * ⭐ board 2812 M4 fix round (C2): it takes a NAME rather than the column's group, so a
 * home's page keeps a title when the column's read has not landed or has failed (the
 * host passes the last known name, else the account's, else "This home"); the
 * subtitle needs the group's town and is left out without it.
 */
export function HomeHeading({
  name,
  subtitle,
  detailsOpen,
  onDetails,
}: {
  name: string;
  subtitle: string | null;
  detailsOpen: boolean;
  onDetails: () => void;
}) {
  return (
    <div class="book-list-head">
      {/* ⭐ The subtitle sits UNDER the title inside the heading row, beside Details:
          measured on the M4 seat at 1024x700, a line of its own put an open card's gold
          primary 5px below the ~700 plane on a home's page. */}
      <div class="homes-title">
        <h1 class="screen-title" tabIndex={-1}>{name}</h1>
        {subtitle !== null && <p class="helper-note homes-subtitle">{subtitle}</p>}
      </div>
      {/* ⭐ R-2812-e: Details is a quiet button beside the title, never a tab. It
          reveals the home's settings in place of the tab's list.
          ⭐ board 2812 M5 (fix round 1, C2): OPEN, it wears the filter chip's own pressed
          classes, composed the way BandControl's band chips are (`chip-btn--active`,
          components.css), so no second copy of that paint is written in app.css. */}
      <button
        type="button"
        class={`btn btn--quiet chip-btn${detailsOpen ? ' chip-btn--active' : ''}`}
        aria-expanded={detailsOpen ? 'true' : 'false'}
        onClick={onDetails}
      >
        {HOME_PAGE_WORDS.details}
      </button>
    </div>
  );
}

/**
 * A home's Details: its account's fields, then its portal home's settings. The account
 * is edited through AccountList's own form (`editOnly`: the form opens on Edit, and there
 * is no Add form on a home's page); the portal blocks come from the host.
 */
export function HomeDetails({
  account,
  book,
  portalBlocks,
  onBookChanged,
}: {
  /** As `HomePrices`: `undefined` while the accounts read has not answered. */
  account: ApiAccount | null | undefined;
  book: AccountBookData;
  /** The host's own blocks for the portal homes linked to this account, or null when
   *  none is linked. */
  portalBlocks: ComponentChildren | null;
  onBookChanged: () => void;
}) {
  return (
    <section class="op-review" aria-label={HOME_PAGE_WORDS.detailsTitle}>
      <h2 class="screen-sub">{HOME_PAGE_WORDS.detailsTitle}</h2>
      {account === undefined ? (
        <p class="helper-note" role="status">{HOME_PAGE_WORDS.accountLoading}</p>
      ) : account === null ? (
        <Banner kind="error">{HOME_PAGE_WORDS.accountUnread}</Banner>
      ) : (
        <AccountList
          accounts={[account]}
          billingParties={book.billingParties}
          emptinessUnknown={!book.loaded || book.failed.accounts}
          onChanged={onBookChanged}
          editOnly
        />
      )}
      {portalBlocks === null ? <p class="helper-note">{HOME_PAGE_WORDS.noPortalHome}</p> : portalBlocks}
    </section>
  );
}

/**
 * All homes' Prices: each home with its price count, from the account book's one price
 * read. A home opens its own Prices tab. ⛔ The count is a COUNT, never an id.
 */
export function HomesPriceCounts({
  groups,
  accounts,
  book,
  onOpen,
}: {
  groups: readonly ApiHomeGroup[];
  accounts: readonly ApiAccount[] | null | undefined;
  book: AccountBookData;
  onOpen: (key: string) => void;
}) {
  const unread = book.loaded && (book.failed.prices || book.failed.accounts);
  const counts = new Map<number, number>();
  for (const p of book.prices) counts.set(p.accountId, (counts.get(p.accountId) ?? 0) + 1);
  return (
    <>
      <p class="helper-note">{HOME_PAGE_WORDS.pricesIntro}</p>
      {unread && <Banner kind="error">{HOME_PAGE_WORDS.pricesUnread}</Banner>}
      {book.truncated.prices && <Banner kind="warn">{HOME_PAGE_WORDS.pricesCut}</Banner>}
      {/* ⭐ board 2812 M4 fix round (L9): the SHARED parts, a `List` of `Row`s, the count in
          the money slot. ⛔ S7: a row's text is a home's name, and `Row` and `List`
          carry `data-fb-opaque`, so the click recorder records THAT a home was opened,
          never WHICH.
          ⭐ board 2812 M5 step 0 (d): the TOWN fills the Row's first column (an empty
          `when` indented every home by that column), and a home with no town says so
          there, so no row has an empty first column. */}
      <List label="Prices for each home" beside>
        {groups.map((g) => {
          const account = accountForKey(accounts, g.key);
          const n = account === null ? null : counts.get(account.accountId) ?? 0;
          return (
            <Row
              key={g.key}
              when={g.town === null || g.town === '' ? HOME_PAGE_WORDS.noTown : g.town}
              who={g.name}
              money={!book.loaded || unread || n === null ? undefined : priceCountWords(n)}
              onOpen={() => onOpen(g.key)}
            />
          );
        })}
      </List>
    </>
  );
}

/** A home's Prices: 2705's `PriceList` for that one account, its words and routes kept. */
export function HomePrices({
  account,
  book,
  onBookChanged,
}: {
  /** ⭐ board 2812 M4 fix round: `undefined` while the accounts read has not answered
   *  (it says so, never "could not be read"); `null` when it failed or has no such home. */
  account: ApiAccount | null | undefined;
  book: AccountBookData;
  onBookChanged: () => void;
}) {
  if (account === undefined) return <p class="helper-note" role="status">{HOME_PAGE_WORDS.accountLoading}</p>;
  if (account === null) return <Banner kind="error">{HOME_PAGE_WORDS.accountUnread}</Banner>;
  const failed = book.loaded && (book.failed.prices || book.failed.accounts || book.failed.destinations);
  return (
    <>
      {failed && (
        <Banner kind="error">
          Some of this could not be loaded, so what is on screen may be incomplete. Nothing has been deleted.{' '}
          {/* ⭐ board 2812 M4 fix round (L9): the shared `Button`, quiet and inline. */}
          <Button variant="quiet" size="inline" onClick={onBookChanged}>Try again</Button>
        </Banner>
      )}
      <PriceList
        prices={book.prices}
        onlyAccountId={account.accountId}
        accounts={[account]}
        destinations={book.destinations}
        emptinessUnknown={!book.loaded || book.failed.prices}
        meta={book.meta}
        pricesTruncated={book.truncated.prices}
        onChanged={onBookChanged}
      />
    </>
  );
}
